import React, { useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera, faSpinner } from '@fortawesome/free-solid-svg-icons';
import api from '../Api';
import ProfileAvatar from './ProfileAvatar';

const MAX_UPLOAD_MB = 5;
const CLIENT_MAX_SIDE = 1024; // downscale before upload; the server still re-encodes to 512

/**
 * Self-service profile photo block for a prestataire / enquêteur.
 *
 * The picture is downscaled in the browser before being sent — a phone photo can be
 * 5–10 MB and fieldworkers are often on mobile data. The server normalises it again
 * (square crop, 512 px, metadata stripped), so this is bandwidth courtesy, not security.
 */
const ProfilePhotoUploader = ({ personType, name, initialPhotoUpdatedAt = null, onChange }) => {
  const [photoUpdatedAt, setPhotoUpdatedAt] = useState(initialPhotoUpdatedAt);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const inputRef = useRef(null);

  const downscale = (file) => new Promise((resolve) => {
    // Anything we can't decode is sent untouched; the server validates it anyway.
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const side = Math.max(img.width, img.height);
      if (side <= CLIENT_MAX_SIDE) return resolve(file);
      const scale = CLIENT_MAX_SIDE / side;
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => resolve(blob ? new File([blob], 'photo.jpg', { type: 'image/jpeg' }) : file),
        'image/jpeg',
        0.9
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;

    setError('');
    setSuccess('');

    if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
      setError(`La photo ne doit pas dépasser ${MAX_UPLOAD_MB} Mo.`);
      return;
    }

    try {
      setBusy(true);
      const prepared = await downscale(file);
      const form = new FormData();
      form.append('photo', prepared, 'photo.jpg');
      const res = await api.post(`/${personType}/profile/photo`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data?.success) {
        const stamp = res.data.data?.photo_updated_at || new Date().toISOString();
        setPhotoUpdatedAt(stamp);
        setSuccess('Photo de profil mise à jour.');
        if (onChange) onChange(stamp);
      } else {
        setError(res.data?.message || 'Erreur lors de l\'envoi de la photo.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de l\'envoi de la photo.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-6 mb-6">
      <div className="flex flex-col sm:flex-row sm:items-center gap-5">
        <ProfileAvatar
          personType={personType}
          name={name}
          photoUpdatedAt={photoUpdatedAt}
          size="xl"
          self
          className="ring-2 ring-white shadow-md"
        />

        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-gray-900">Photo de profil</h3>
          <p className="text-sm text-gray-600 mt-1">
            Formats acceptés : JPG, PNG ou WEBP, {MAX_UPLOAD_MB} Mo maximum.
          </p>

          <div className="flex flex-wrap gap-2 mt-3">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={busy}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
            >
              <FontAwesomeIcon icon={busy ? faSpinner : faCamera} className={busy ? 'animate-spin' : ''} />
              {photoUpdatedAt ? 'Changer la photo' : 'Ajouter une photo'}
            </button>

          </div>

          {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
          {success && <p className="mt-3 text-sm text-green-600">{success}</p>}

          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFile}
            className="hidden"
          />
        </div>
      </div>
    </div>
  );
};

export default ProfilePhotoUploader;
